import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'

export default function Superviseurs() {
  const [superviseurs, setSuperviseurs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ prenom:'', nom:'', email:'', password:'', taux:0.3520, seuil:185 })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [resetId, setResetId] = useState(null)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('*, superviseurs(taux_remuneration, seuil_minimum)')
      .eq('role', 'superviseur')
      .order('prenom')
    setSuperviseurs(data || [])
    setLoading(false)
  }

  async function handleCreate(e) {
    e.preventDefault()
    setSaving(true)
    setMsg(null)

    const { data: { session } } = await supabase.auth.getSession()

    const res = await fetch('/.netlify/functions/create-superviseur', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify(form)
    })

    const result = await res.json()

    if (!res.ok || result.error) {
      setMsg({ type: 'error', text: result.error || 'Erreur lors de la création.' })
    } else {
      setMsg({ type: 'success', text: `${form.prenom} ${form.nom} créé avec succès.` })
      setShowForm(false)
      setForm({ prenom:'', nom:'', email:'', password:'', taux:0.3520, seuil:185 })
      load()
    }
    setSaving(false)
  }

  async function toggleActif(sup) {
    await supabase.from('profiles').update({ actif: !sup.actif }).eq('id', sup.id)
    load()
  }

  async function handleResetPassword(sup) {
    if (!window.confirm(`Générer un nouveau mot de passe pour ${sup.prenom} ${sup.nom} et l'envoyer à ${sup.email} ?\n\nL'ancien mot de passe ne fonctionnera plus.`)) return
    setResetId(sup.id)
    setMsg(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/reset-password-superviseur', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ id: sup.id })
      })
      const result = await res.json().catch(() => ({}))
      if (!res.ok || result.error) {
        setMsg({ type: 'error', text: result.error || 'Erreur lors de la réinitialisation.' })
      } else {
        setMsg({ type: 'success', text: `Nouveau mot de passe envoyé à ${result.email}.` })
      }
    } catch {
      setMsg({ type: 'error', text: 'Erreur réseau. Réessayez.' })
    }
    setResetId(null)
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Superviseurs</h2>
          <div className="page-header-sub">{superviseurs.length} superviseur{superviseurs.length > 1 ? 's' : ''} enregistré{superviseurs.length > 1 ? 's' : ''}</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setMsg(null) }}>
          {showForm ? 'Annuler' : '+ Nouveau superviseur'}
        </button>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      {showForm && (
        <div className="card" style={{marginBottom:20}}>
          <h3 style={{marginBottom:20}}>Nouveau superviseur</h3>
          <form onSubmit={handleCreate}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 20px'}}>
              <div className="form-group">
                <label>Prénom</label>
                <input value={form.prenom} onChange={e => setForm(f=>({...f,prenom:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Nom</label>
                <input value={form.nom} onChange={e => setForm(f=>({...f,nom:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Mot de passe provisoire</label>
                <input type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} required minLength={6} placeholder="6 caractères minimum" />
              </div>
              <div className="form-group">
                <label>Taux de rémunération</label>
                <input type="number" step="0.0001" min="0" max="1" value={form.taux}
                  onChange={e => setForm(f=>({...f,taux:parseFloat(e.target.value)}))} />
              </div>
              <div className="form-group">
                <label>Seuil minimum (€)</label>
                <input type="number" min="0" value={form.seuil}
                  onChange={e => setForm(f=>({...f,seuil:parseFloat(e.target.value)}))} />
              </div>
            </div>
            <div style={{display:'flex', gap:10, marginTop:4}}>
              <button type="submit" className="btn btn-bordeaux" disabled={saving}>
                {saving ? 'Création...' : 'Créer le compte'}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Annuler</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Superviseur</th>
              <th>Email</th>
              <th className="num">Taux</th>
              <th className="num">Seuil minimum</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {superviseurs.length === 0 ? (
              <tr><td colSpan={6} style={{textAlign:'center', color:'var(--stone)', padding:32}}>Aucun superviseur enregistré</td></tr>
            ) : superviseurs.map(s => (
              <tr key={s.id}>
                <td><strong>{s.prenom} {s.nom}</strong></td>
                <td className="text-muted">{s.email}</td>
                <td className="num">{((s.superviseurs?.taux_remuneration || 0.352) * 100).toFixed(2)} %</td>
                <td className="num">{s.superviseurs?.seuil_minimum || 185} €</td>
                <td><span className={`badge badge-${s.actif ? 'actif' : 'inactif'}`}>{s.actif ? 'Actif' : 'Inactif'}</span></td>
                <td style={{textAlign:'right'}}>
                  <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleResetPassword(s)}
                      disabled={resetId === s.id || !s.email}
                      title="Génère un nouveau mot de passe et l'envoie par email"
                    >
                      {resetId === s.id ? 'Envoi...' : 'Nouveau mot de passe'}
                    </button>
                    <button
                      className={`btn btn-sm ${s.actif ? 'btn-danger-ghost' : 'btn-outline'}`}
                      onClick={() => toggleActif(s)}
                    >
                      {s.actif ? 'Désactiver' : 'Réactiver'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
